import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, Flame, ArrowRight, Zap, Target, Activity, Trophy, Brain, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const mangaUrl = (file) => `${BACKEND_URL}/api/manga/${file}`;

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const slideInRight = {
  hidden: { opacity: 0, x: 60 },
  show: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const slideInLeft = {
  hidden: { opacity: 0, x: -60 },
  show: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const startTraining = () => navigate("/login?mode=register");
  const goLogin = () => navigate("/login");
  const goSubscribe = () => navigate("/login?mode=register");

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-orange-500 selection:text-black">
      {/* NAV */}
      <header
        data-testid="main-navigation"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-black/70 backdrop-blur-xl border-b border-white/5"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/logo-master.png" alt="Pivot Performance" className="h-8 w-8 object-contain" />
            <span className="font-heading font-black text-lg tracking-tight uppercase">
              Pivot <span className="text-orange-500">Performance</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-widest font-mono text-zinc-400">
            <a href="#story" className="hover:text-orange-500 transition" data-testid="nav-story-link">Story</a>
            <a href="#arsenal" className="hover:text-orange-500 transition" data-testid="nav-arsenal-link">Arsenal</a>
            <a href="#pricing" className="hover:text-orange-500 transition" data-testid="nav-pricing-link">Pricing</a>
            <a href="#faq" className="hover:text-orange-500 transition" data-testid="nav-faq-link">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={goLogin}
              className="hidden sm:inline-flex text-xs uppercase tracking-widest font-mono text-zinc-400 hover:text-white px-3 py-2 transition"
              data-testid="nav-login-btn"
            >
              Login
            </button>
            <button
              onClick={startTraining}
              className="text-xs sm:text-sm uppercase tracking-widest font-heading font-black bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 transition btn-tactical"
              data-testid="nav-cta-btn"
            >
              Start Training
            </button>
          </div>
        </div>
      </header>

      {/* HERO — THE CUT */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-end pb-20 md:pb-32 overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://static.prod-images.emergentagent.com/jobs/51133edb-2580-4347-bb95-90a793d3d461/images/4ad68b997e73ee77457170e63faed13ed929e606b03fcdf696124f9a8e4e1d5d.png)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.6)_80%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-3xl">
            <p className="text-xs md:text-sm font-mono uppercase tracking-[0.3em] text-orange-500 mb-5 flex items-center gap-2">
              <span className="inline-block w-8 h-px bg-orange-500" />
              Season Zero Live
            </p>
            <h1 className="font-heading font-black uppercase leading-[0.9] tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
              They Told <br /> You <span className="text-orange-500">No.</span>
            </h1>
            <p className="mt-6 text-base md:text-xl text-zinc-300 max-w-2xl leading-relaxed">
              They said you weren't tall enough. Fast enough. <span className="text-white font-semibold">Good enough.</span> They gave your spot to someone else. <span className="text-orange-500 font-bold">Good.</span> Now the real work begins.
            </p>
            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3">
              <button
                onClick={startTraining}
                className="group inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-heading font-black uppercase tracking-wider text-base md:text-lg px-6 md:px-8 py-4 btn-tactical shadow-[0_0_30px_rgba(249,115,22,0.35)] hover:shadow-[0_0_45px_rgba(249,115,22,0.6)] transition-all"
                data-testid="hero-cta-btn"
              >
                Start Training
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#story"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-zinc-700 hover:border-orange-500 text-zinc-300 hover:text-orange-500 font-heading font-black uppercase tracking-wider text-base md:text-lg px-6 md:px-8 py-4 transition-all"
                data-testid="hero-secondary-cta-btn"
              >
                See The Story
              </a>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-6 text-xs uppercase tracking-widest font-mono text-zinc-500">
              <span className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-orange-500" /> 87 Drills
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-orange-500" /> 23-Week Program
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-orange-500" /> AI Vertical Tracker
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STORY ANCHOR */}
      <div id="story" />

      {/* CHAPTER 1 — THE GRIND */}
      <section
        data-testid="story-grind-section"
        className="relative py-24 md:py-32 border-t border-zinc-900"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={slideInLeft}
          >
            <p className="text-xs md:text-sm font-mono uppercase tracking-[0.3em] text-orange-500 mb-5">
              Chapter 01
            </p>
            <h2 className="font-heading font-black uppercase leading-[0.9] tracking-tighter text-5xl md:text-6xl mb-6">
              The <span className="text-orange-500">Grind.</span>
            </h2>
            <p className="text-base md:text-lg text-zinc-300 leading-relaxed mb-4">
              6 AMs in an empty driveway. Lungs burning. Legs shaking. Sweat dripping onto concrete that doesn't care if you quit.
            </p>
            <p className="text-base md:text-lg text-zinc-400 leading-relaxed mb-8">
              You don't build an engine under the bright lights. You build it in the dark. <span className="text-white font-semibold">When nobody is watching.</span> When every part of you says rest, and you say <span className="text-orange-500 font-bold">one more rep.</span>
            </p>
            <button
              onClick={startTraining}
              className="inline-flex items-center gap-2 text-orange-500 hover:text-white font-heading font-black uppercase tracking-wider text-sm border-b-2 border-orange-500 hover:border-white pb-1 transition-all"
              data-testid="story-grind-cta-btn"
            >
              Build Your Engine <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={slideInRight}
            className="relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-tr from-orange-600/20 to-transparent blur-2xl" />
            <div className="relative bg-zinc-950 border border-zinc-800 p-6 md:p-10">
              <img
                src={mangaUrl("sprint.png")}
                alt="Sprint drill"
                className="w-full h-auto object-contain"
                onError={(e) => { e.target.style.display = "none"; }}
              />
              <div className="mt-4 flex items-center justify-between border-t border-zinc-900 pt-4">
                <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Drill 01 — Sprint</span>
                <span className="text-xs font-mono uppercase tracking-widest text-orange-500">5 AM</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CHAPTER 2 — THE RETURN */}
      <section
        data-testid="story-return-section"
        className="relative py-24 md:py-32 border-t border-zinc-900"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={slideInLeft}
            className="md:order-2"
          >
            <p className="text-xs md:text-sm font-mono uppercase tracking-[0.3em] text-orange-500 mb-5">
              Chapter 02
            </p>
            <h2 className="font-heading font-black uppercase leading-[0.9] tracking-tighter text-5xl md:text-6xl mb-6">
              The <span className="text-orange-500">Return.</span>
            </h2>
            <p className="text-base md:text-lg text-zinc-300 leading-relaxed mb-4">
              Next season isn't a tryout. It's an ambush.
            </p>
            <p className="text-base md:text-lg text-zinc-400 leading-relaxed mb-8">
              The coach who cut you watches the player you became — and doesn't recognize him. <span className="text-white font-semibold">Become undeniable.</span> Turn every setback into the foundation of your <span className="text-orange-500 font-bold">comeback.</span>
            </p>
            <button
              onClick={startTraining}
              className="inline-flex items-center gap-2 text-orange-500 hover:text-white font-heading font-black uppercase tracking-wider text-sm border-b-2 border-orange-500 hover:border-white pb-1 transition-all"
              data-testid="story-return-cta-btn"
            >
              Make Your Comeback <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={slideInRight}
            className="md:order-1 relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-bl from-orange-600/20 to-transparent blur-2xl" />
            <div className="relative bg-zinc-950 border border-zinc-800 p-6 md:p-10">
              <img
                src={mangaUrl("box_jump_main.png")}
                alt="Box jump"
                className="w-full h-auto object-contain"
                onError={(e) => { e.target.style.display = "none"; }}
              />
              <div className="mt-4 flex items-center justify-between border-t border-zinc-900 pt-4">
                <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Drill 47 — Box Jump</span>
                <span className="text-xs font-mono uppercase tracking-widest text-orange-500">+4" Vertical</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* THE ARSENAL — FEATURES */}
      <section
        id="arsenal"
        data-testid="features-section"
        className="relative py-24 md:py-32 border-t border-zinc-900"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="mb-12 md:mb-16 max-w-3xl"
          >
            <p className="text-xs md:text-sm font-mono uppercase tracking-[0.3em] text-orange-500 mb-5">
              Chapter 03 — The Arsenal
            </p>
            <h2 className="font-heading font-black uppercase leading-[0.9] tracking-tighter text-5xl md:text-6xl mb-4">
              Tools That Don't <br /> <span className="text-orange-500">Quit on You.</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            <FeatureCard
              span="md:col-span-7"
              icon={<Activity className="w-6 h-6" />}
              kicker="Live"
              title="Engine Monitor"
              body="Real-time conditioning workouts. Timer-driven sessions that push you exactly to your edge — no more, no less."
              tall
              testId="feature-engine"
            />
            <FeatureCard
              span="md:col-span-5"
              icon={<Zap className="w-6 h-6" />}
              kicker="AI"
              title="Vertical Tracker"
              body="Just your phone. Just your jump. AI measures your vertical to the inch — no equipment required."
              tall
              testId="feature-vertical"
            />
            <FeatureCard
              span="md:col-span-5"
              icon={<Target className="w-6 h-6" />}
              kicker="Library"
              title="87 Drills"
              body="Sprints. Plyos. Strength. Each with step-by-step manga panels for proper form."
              testId="feature-drills"
            />
            <FeatureCard
              span="md:col-span-7"
              icon={<Brain className="w-6 h-6" />}
              kicker="Pro"
              title="AI Coach"
              body="GPT-5.2, Claude, and Gemini in your pocket. Get personalized recommendations, weekly plans, and answers to 'should I rest today?'"
              testId="feature-ai-coach"
            />
            <FeatureCard
              span="md:col-span-4"
              icon={<Trophy className="w-6 h-6" />}
              kicker="Earn"
              title="Badges & Leaderboards"
              body="Every PR is a notch. Compete with athletes building their summer."
              testId="feature-badges"
            />
            <FeatureCard
              span="md:col-span-4"
              icon={<Flame className="w-6 h-6" />}
              kicker="23 Weeks"
              title="Break the Cycle"
              body="The flagship program. Foundation → Strength → Power → Beast Mode."
              testId="feature-program"
            />
            <FeatureCard
              span="md:col-span-4"
              icon={<ShieldCheck className="w-6 h-6" />}
              kicker="Recovery"
              title="Recovery Coach"
              body="Sleep, soreness, hydration — all tracked. Train hard. Recover harder."
              testId="feature-recovery"
            />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        data-testid="testimonials-section"
        className="relative py-24 md:py-32 border-t border-zinc-900 bg-zinc-950/40"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="mb-12 max-w-3xl"
          >
            <p className="text-xs md:text-sm font-mono uppercase tracking-[0.3em] text-orange-500 mb-5">
              The Comeback Stories
            </p>
            <h2 className="font-heading font-black uppercase leading-[0.9] tracking-tighter text-4xl md:text-5xl">
              They Got Cut Too.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <Quote
              text="Got cut junior year. Spent the whole summer on Pivot. Made varsity senior year and started 18 games."
              name="Marcus T."
              role="Senior Guard · Texas"
            />
            <Quote
              text="Finally a program that doesn't waste my time with fluff. Just raw work. Added 4 inches to my vertical in 12 weeks."
              name="David L."
              role="Forward · California"
            />
            <Quote
              text="The AI Coach actually adjusts based on how I feel. It's like having a trainer in my pocket — except I pay $14 a month instead of $200 a session."
              name="Sarah J."
              role="Point Guard · New York"
            />
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section
        id="pricing"
        data-testid="pricing-section"
        className="relative py-24 md:py-32 border-t border-zinc-900"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="mb-12 md:mb-16 text-center max-w-3xl mx-auto"
          >
            <p className="text-xs md:text-sm font-mono uppercase tracking-[0.3em] text-orange-500 mb-5">
              Chapter 04
            </p>
            <h2 className="font-heading font-black uppercase leading-[0.9] tracking-tighter text-5xl md:text-6xl mb-4">
              No Excuses. <br /> <span className="text-orange-500">Pick Your Weapon.</span>
            </h2>
            <p className="text-base text-zinc-400 mt-4">Cancel anytime. Billed monthly. No lock-ins.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto">
            <PricingCard
              name="Basic"
              price="9.99"
              tier="basic"
              features={[
                "23-week program",
                "87 drills + manga guides",
                "Engine Monitor",
                "Vertical Tracker (manual)",
                "Recovery logs",
                "Voice Coach (Coach)",
              ]}
              onClick={goSubscribe}
              testId="pricing-basic-btn"
            />
            <PricingCard
              name="Pro"
              price="14.99"
              tier="pro"
              highlight
              features={[
                "Everything in Basic",
                "AI Vertical Measure (phone)",
                "AI Coach (GPT-5.2, Claude)",
                "AI Drill Analyzer (3/mo)",
                "Weekly plan generator",
                "All 4 voice styles",
              ]}
              onClick={goSubscribe}
              testId="pricing-pro-btn"
            />
            <PricingCard
              name="Elite"
              price="24.99"
              tier="elite"
              features={[
                "Everything in Pro",
                "Unlimited AI analyses",
                "Beast Mode challenges",
                "Team challenges",
                "Leaderboard priority",
                "Early feature access",
              ]}
              onClick={goSubscribe}
              testId="pricing-elite-btn"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        data-testid="faq-section"
        className="relative py-24 md:py-32 border-t border-zinc-900"
      >
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="mb-10"
          >
            <p className="text-xs md:text-sm font-mono uppercase tracking-[0.3em] text-orange-500 mb-5">
              Intel.
            </p>
            <h2 className="font-heading font-black uppercase leading-[0.9] tracking-tighter text-5xl md:text-6xl">
              Questions.
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-2">
            <FAQItem
              q="Is this just for high school players?"
              a="No. Pivot Performance is for anyone who wants to get better — middle school, high school, college, AAU, adult rec leagues, weekend warriors. The 23-week program adapts to your baseline. The drills work whether you're 13 or 40."
            />
            <FAQItem
              q="Do I need a gym or equipment?"
              a="No. We have a complete bodyweight track for athletes training at home or in a driveway. Access to a court and weights unlocks more drills, but you can absolutely build a serious engine with just yourself and a flat surface."
            />
            <FAQItem
              q="How accurate is the AI Vertical Measure?"
              a="±1–2 inches with proper setup (phone propped 5–10 ft away, full body in frame, side profile). It's not a force plate, but it's WAY better than guessing — and you can re-measure any time, free, for the rest of your subscription."
            />
            <FAQItem
              q="Can I really cancel anytime?"
              a="Yes. Click 'Manage Billing' inside the app → cancel → keep access until your current period ends. No phone calls, no retention scripts."
            />
            <FAQItem
              q="What if I'm under 18?"
              a="You must be 13+ to use Pivot Performance (COPPA). If you're under 18, please get a parent's permission before subscribing — they're the ones being charged."
            />
            <FAQItem
              q="Does this replace a real coach?"
              a="No, and we'd never claim it does. But it gives you something most athletes don't have: a structured program, real-time feedback, and a way to put in serious reps when your coach isn't around. The coach develops you 2 hours a day. We help with the other 22."
            />
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        data-testid="final-cta-section"
        className="relative py-24 md:py-32 border-t border-zinc-900 bg-gradient-to-b from-[#0a0a0a] via-orange-950/20 to-[#0a0a0a]"
      >
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
          >
            <p className="text-xs md:text-sm font-mono uppercase tracking-[0.3em] text-orange-500 mb-5">
              The Last Page.
            </p>
            <h2 className="font-heading font-black uppercase leading-[0.9] tracking-tighter text-5xl md:text-7xl mb-6">
              Generational <br /> <span className="text-orange-500">Summer.</span>
            </h2>
            <p className="text-base md:text-xl text-zinc-300 max-w-2xl mx-auto mb-10 leading-relaxed">
              Everyone starts somewhere. Most stop there. <span className="text-white font-semibold">You're not most.</span>
            </p>
            <button
              onClick={startTraining}
              className="inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-heading font-black uppercase tracking-wider text-lg md:text-2xl px-10 md:px-14 py-5 md:py-6 btn-tactical shadow-[0_0_40px_rgba(249,115,22,0.4)] hover:shadow-[0_0_60px_rgba(249,115,22,0.7)] transition-all"
              data-testid="final-cta-btn"
            >
              Start Training
              <ArrowRight className="w-6 h-6" />
            </button>
            <p className="mt-5 text-xs font-mono uppercase tracking-widest text-zinc-600">
              No credit card to sign up · Free 13+ account
            </p>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer
        data-testid="footer-section"
        className="border-t border-zinc-900 py-12 px-5 md:px-8"
      >
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src="/logo-master.png" alt="Pivot Performance" className="h-7 w-7 object-contain" />
            <div>
              <p className="font-heading font-black text-sm uppercase tracking-tight">
                Pivot Performance
              </p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                © {new Date().getFullYear()} Pivot Point Athletics, LLC
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 md:gap-6 text-xs font-mono uppercase tracking-widest text-zinc-500">
            <button onClick={goLogin} className="hover:text-orange-500 transition" data-testid="footer-login-link">Login</button>
            <Link to="/privacy" className="hover:text-orange-500 transition" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/terms" className="hover:text-orange-500 transition" data-testid="footer-terms-link">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

/* ------------ Subcomponents ------------ */

const FeatureCard = ({ span, icon, kicker, title, body, tall, testId }) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-50px" }}
    variants={fadeUp}
    className={`${span || "md:col-span-4"}`}
  >
    <Card
      data-testid={testId}
      className={`group relative bg-zinc-950 border border-zinc-800 hover:border-orange-500/40 rounded-none transition-all duration-300 overflow-hidden h-full ${tall ? "min-h-[200px]" : ""}`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 via-orange-500/0 to-orange-500/0 group-hover:from-orange-500/5 group-hover:to-transparent transition-all duration-500" />
      <CardContent className="relative p-6 md:p-8 h-full flex flex-col">
        <div className="flex items-center justify-between mb-5">
          <div className="w-10 h-10 bg-orange-500/10 border border-orange-500/30 flex items-center justify-center text-orange-500 group-hover:bg-orange-500/20 transition">
            {icon}
          </div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-orange-500/70">
            {kicker}
          </span>
        </div>
        <h3 className="font-heading font-black uppercase text-2xl md:text-3xl tracking-tight leading-tight mb-3 text-white">
          {title}
        </h3>
        <p className="text-sm md:text-base text-zinc-400 leading-relaxed">{body}</p>
      </CardContent>
    </Card>
  </motion.div>
);

const Quote = ({ text, name, role }) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-50px" }}
    variants={fadeUp}
  >
    <div className="bg-zinc-950 border border-zinc-800 p-6 md:p-8 h-full flex flex-col">
      <span className="text-orange-500 text-5xl leading-none font-heading mb-3">"</span>
      <p className="text-base text-zinc-300 leading-relaxed mb-6 flex-1">{text}</p>
      <div className="border-t border-zinc-900 pt-4">
        <p className="font-heading font-black uppercase text-sm tracking-tight text-white">
          {name}
        </p>
        <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
          {role}
        </p>
      </div>
    </div>
  </motion.div>
);

const PricingCard = ({ name, price, features, onClick, highlight, testId }) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-50px" }}
    variants={fadeUp}
    className={`relative ${highlight ? "md:-mt-4" : ""}`}
  >
    {highlight && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-orange-600 text-white text-[10px] font-mono uppercase tracking-widest px-3 py-1 z-10">
        Most Popular
      </div>
    )}
    <div
      className={`relative h-full p-6 md:p-8 border-2 transition-all ${
        highlight
          ? "bg-zinc-950 border-orange-500 shadow-[0_0_40px_rgba(249,115,22,0.2)]"
          : "bg-zinc-950 border-zinc-800 hover:border-zinc-700"
      }`}
    >
      <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">
        {name === "Basic" ? "Starter" : name === "Pro" ? "Recommended" : "All Access"}
      </p>
      <h3 className="font-heading font-black uppercase text-4xl tracking-tight text-white mb-2">
        {name}
      </h3>
      <div className="flex items-baseline gap-1 mb-6">
        <span className="text-5xl font-heading font-black text-white">${price}</span>
        <span className="text-zinc-500 text-sm font-mono uppercase tracking-widest">/mo</span>
      </div>
      <button
        onClick={onClick}
        className={`w-full font-heading font-black uppercase tracking-wider text-sm py-3 mb-6 transition-all btn-tactical ${
          highlight
            ? "bg-orange-600 hover:bg-orange-500 text-white"
            : "bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-700"
        }`}
        data-testid={testId}
      >
        Subscribe →
      </button>
      <ul className="space-y-3">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
            <Check className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  </motion.div>
);

const FAQItem = ({ q, a }) => (
  <AccordionItem value={q} className="border border-zinc-800 bg-zinc-950 px-5 md:px-6">
    <AccordionTrigger className="font-heading font-black uppercase tracking-tight text-base md:text-lg text-white hover:text-orange-500 hover:no-underline py-5">
      {q}
    </AccordionTrigger>
    <AccordionContent className="text-zinc-400 leading-relaxed pb-5">
      {a}
    </AccordionContent>
  </AccordionItem>
);

export default LandingPage;
